// Entry for an org's storefront on its own custom domain (served as site.html).
//
// A visitor to e.g. sleepycircuits.com gets the website and nothing else: no
// Matrix, no chat, no shader engine, no app store, no service-worker precache.
// Sign-in, the user menu and in-place editing arrive later as a lazy account
// layer (features/site/SiteAccountSlot), only for a visitor who has a stored
// session or asks to sign in.
import React, { Suspense } from 'react';
import { BootSplash } from './components/BootSplash';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { SitePage } from './features/site/SitePage';
import { readSiteBoot } from './services/siteBoot';
import { ensureEditorFonts } from './utils/editorFonts';
import { fireVisitBeacon } from './utils/visitBeacon';
import './index.css';

const NewsletterSignupPage = React.lazy(() => import('./features/newsletter/NewsletterSignupPage'));

/** The loading screen while a lazy route loads. */
const ROUTE_FALLBACK = <BootSplash />;

const StorefrontApp: React.FC<{ orgSlug: string }> = ({ orgSlug }) => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<SitePage domainOrgSlug={orgSlug} />} />
      {/* Must precede the /:slug catch-all so 'newsletter' isn't matched as a
          storefront page slug. */}
      <Route path="/newsletter" element={<Suspense fallback={ROUTE_FALLBACK}><NewsletterSignupPage orgSlug={orgSlug} /></Suspense>} />
      <Route path="/:slug" element={<SitePage domainOrgSlug={orgSlug} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </BrowserRouter>
);

/**
 * The server only serves this entry for a host it resolved to an org, and says
 * which org in the page. If that is missing (a stale cached page, a proxy that
 * stripped it) fall back to the full app, which probes the host itself.
 */
// The theme's web fonts, as early as the bundle runs (site.html can't: its CSP refuses inline handlers).
ensureEditorFonts();

const boot = readSiteBoot();
if (boot) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <AppErrorBoundary>
      <StorefrontApp orgSlug={boot.orgSlug} />
    </AppErrorBoundary>,
  );
  fireVisitBeacon();
} else {
  void import('./main');
}
